import { create } from "zustand";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import type { TFunction } from "i18next";
import { type UseFormReset } from "react-hook-form";
import api, { isAbortError } from "../../utils/api";
import type { CancelTokenSource } from "axios";
import axios from "axios";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface TripAssignment {
  id?: string;
  device_id: string;
  date: string;
  start_time: string;
  end_time: string;
  status: "pending" | "active" | "completed" | "cancelled";
  timezone?: string;
  device?: {
    id: string;
    name?: string;
    identifier?: string;
    vehicle_id?: string | null;
    remolque_id?: string | null;
  } | null;
  /**
   * Conductor resuelto en el backend para este assignment.
   *
   * Cadena: assignment.device_id → device.vehicle_id → drivers donde
   * `vehicle_id` coincide → driver.userId → users/{userId}.email.
   *
   * Lo poblamos en `tripsController` (`/tripsList` y `/trips`) para que el
   * chat del viaje pueda preseleccionar drivers como participantes sin
   * depender del realtime layer (`deviceAssignments` en RTDB), que puede
   * estar vacío si el device nunca pasó por `changeDriver`.
   */
  driver?: {
    id: string | null;
    user_id: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
}

export interface Trip {
  id: string;
  title?: string;
  description?: string;
  route_id?: string;
  status_id?: string;
  priority?: string;
  company_id?: string;
  comment?: string;
  assignments?: TripAssignment[];
  notifiedUsers?: string[];
  createdAt?: { _seconds: number; _nanoseconds: number } | string;
  updatedAt?: { _seconds: number; _nanoseconds: number } | string;
  // Populated relations
  status?: {
    id: string;
    name?: string;
    color?: string;
  } | null;
  route?: {
    id: string;
    name?: string;
    description?: string;
  } | null;
  // Legacy fields (kept for backwards compatibility)
  origin?: string;
  destiny?: string;
  eta?: string;
  vehicle_id?: string;
  remolque_id?: string;
  geofence_ids?: string[];
  geofence_id?: string;
  charge_ids?: string[];
  user_id?: string;
  experience?: string;
  signature?: string;
  startedAt?: string;
  finalizedAt?: string;
  vehicle?: {
    id: string;
    name?: string;
    plate?: string;
    image?: string;
  } | null;
  remolque?: {
    id: string;
    name?: string;
    plate?: string;
  } | null;
  geofence?: {
    id: string;
    name?: string;
    shape?: string;
  } | null;
}

interface TripsMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface TripsStore {
  trips: Trip[];
  loading: boolean;
  error: string | null;
  meta?: TripsMeta;
  cancelToken: CancelTokenSource | null;
  fetchTrips: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status_id?: string;
    search?: string;
    device_id?: string;
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  fetchTrip: (tripId: string) => Promise<{ success: boolean; data?: Trip; error?: string }>;
  updateTripInStore: (tripId: string, updatedTrip: Partial<Trip>) => void;
  addTripToStore: (trip: Trip) => void;
  removeTripFromStore: (tripId: string) => void;
}

export const useTripsStore = create<TripsStore>((set, get) => ({
  trips: [],
  loading: false,
  error: null,
  meta: undefined,
  cancelToken: null,

  fetchTrips: async (params = {}) => {
    const { cancelToken, loading } = get();
    if (cancelToken) {
      cancelToken.cancel("Operation canceled due to new request.");
    }

    if (loading && !cancelToken) return;

    const fetchGen = getAdminFetchGeneration();
    const newCancelToken = axios.CancelToken.source();
    set({ loading: true, error: null, cancelToken: newCancelToken });

    try {
      const { data } = await api.get("/tripsList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
        cancelToken: newCancelToken.token,
      });
      if (getAdminFetchGeneration() !== fetchGen) return;
      set({
        trips: data.data || [],
        meta: data.meta,
        loading: false,
        cancelToken: null,
      });
    } catch (err: unknown) {
      if (axios.isCancel(err) || isAbortError(err) || getAdminFetchGeneration() !== fetchGen) {
        set({ loading: false, cancelToken: null });
        return;
      }
      set({
        error: getError(err),
        loading: false,
        cancelToken: null,
      });
    }
  },

  fetchTrip: async (tripId: string) => {
    try {
      const { data } = await api.get("/trips", { params: { trip_id: tripId } });
      return { success: true, data: data.data };
    } catch (err: unknown) {
      if (isAbortError(err)) return { success: false };
      console.error("Error fetching trip:", err);
      return { success: false, error: getError(err) };
    }
  },

  updateTripInStore: (tripId: string, updatedTrip: Partial<Trip>) => {
    set((state) => ({
      trips: state.trips.map((trip) =>
        trip.id === tripId ? { ...trip, ...updatedTrip } : trip
      ),
    }));
  },

  addTripToStore: (trip: Trip) => {
    set((state) => ({
      trips: [trip, ...state.trips],
      meta: state.meta
        ? {
            ...state.meta,
            count: state.meta.count + 1,
            totalItems: state.meta.totalItems + 1,
          }
        : undefined,
    }));
  },

  removeTripFromStore: (tripId: string) => {
    set((state) => ({
      trips: state.trips.filter((trip) => trip.id !== tripId),
      meta: state.meta
        ? {
            ...state.meta,
            count: Math.max(0, state.meta.count - 1),
            totalItems: Math.max(0, state.meta.totalItems - 1),
          }
        : undefined,
    }));
  },
}));

export const createTrip = async (
  tripData: Record<string, unknown>,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    const { data } = await api.post("/trips", tripData);

    if (data.data) {
      useTripsStore.getState().addTripToStore(data.data);
    }

    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Viaje creado exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const updateTrip = async (
  tripId: string,
  tripData: Record<string, unknown>,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    const { data } = await api.put("/trips", tripData, {
      params: { trip_id: tripId },
    });

    if (data.data) {
      useTripsStore.getState().updateTripInStore(tripId, data.data);
    }

    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Viaje actualizado exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const deleteTrip = async (
  tripId: string,
  showAlert: (config: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
  onSuccess?: () => void
) => {
  try {
    await api.delete("/trips", {
      params: { trip_id: tripId },
    });

    useTripsStore.getState().removeTripFromStore(tripId);

    showAlert({
      title: t("Exito"),
      text: t("Viaje eliminado exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        if (onSuccess) onSuccess();
      },
      showCancelButton: false,
    });
  } catch (error) {
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const resetTrip = async (
  tripId: string
): Promise<{ success: boolean; data?: Trip; error?: string }> => {
  try {
    const { data } = await api.post("/resetTrip", { trip_id: tripId });
    if (data.data) {
      useTripsStore.getState().updateTripInStore(tripId, data.data);
      return { success: true, data: data.data };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: getError(error) };
  }
};

export const getTripById = async (
  tripId: string
): Promise<{ success: boolean; data?: Trip; error?: string }> => {
  try {
    const { data } = await api.get("/trips", {
      params: { trip_id: tripId },
    });
    if (data?.data) {
      return { success: true, data: data.data.data || data.data };
    }
    return { success: false, error: "Trip not found" };
  } catch (error) {
    return { success: false, error: getError(error) };
  }
};
